// <copyright file="UserDashboardCrmFields.ext.tsx" company="clarity-ventures.com">
// Copyright (c) clarity-ventures.com. All rights reserved.
// </copyright>

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRevalidator } from "@remix-run/react";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { EditorSection } from "@core/components/EditorFrame";
import { useApi } from "@core/api";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/Input";
import { Label } from "@core/components/ui/label";
import { useToast } from "@core/components/ui/use-toast";
import type { User } from "@/apidtos";

const CALENDLY_TAG_KEY = "CalendlyLink";
const HEADSHOT_TAG_KEY = "HeadshotFileId";

function tagValue(tags: User["Tags"], key: string): string {
  return tags?.find((t) => t.Key === key)?.Value?.trim() ?? "";
}

function HeadshotPreview({ url, name }: { url: string | null; name?: string | null }) {
  const initials = (name ?? "?")
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className={url ? "dash-crm-avatar dash-crm-avatar--photo" : "dash-crm-avatar"}
      style={{ width: 72, height: 72, marginBottom: 12 }}
    >
      {url ? <img src={url} alt="" /> : initials}
    </div>
  );
}

function UserDashboardCrmFieldsInner({ userId, user }: { userId: number; user: User }) {
  const { t } = useTranslation();
  const api = useApi();
  const { toast } = useToast();
  const revalidator = useRevalidator();

  const [calendlyLink, setCalendlyLink] = useState("");
  const [savingCalendly, setSavingCalendly] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setCalendlyLink(tagValue(user?.Tags, CALENDLY_TAG_KEY));
  }, [user?.Tags]);

  const headshotFileId = tagValue(user?.Tags, HEADSHOT_TAG_KEY);
  const headshotUrl = headshotFileId
    ? `/api/file/${headshotFileId}?v=${cacheBust}`
    : null;

  const saveCalendlyLink = async () => {
    setSavingCalendly(true);
    try {
      const trimmed = calendlyLink.trim();
      const tags = trimmed
        ? [{ Key: CALENDLY_TAG_KEY, Value: trimmed, $op: "upsert" as const }]
        : [{ Key: CALENDLY_TAG_KEY, $op: "delete" as const }];
      await api.UpdateUser({ Id: userId, Tags: tags });
      toast({ title: t("ui.saved", "Saved"), description: "Calendly link updated." });
      revalidator.revalidate();
    } catch (err: unknown) {
      const description = err instanceof Error ? err.message : String(err);
      toast({ title: t("ui.saveFailed", "Save failed"), description, variant: "destructive" });
    } finally {
      setSavingCalendly(false);
    }
  };

  const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.api.post(`/spg/user-headshot/${userId}/upload`, fd, {
        headers: { "Content-Type": undefined },
        transformRequest: (data) => data,
      });
      toast({ title: "Headshot uploaded", description: "CRM headshot image saved on this user." });
      setCacheBust(Date.now());
      revalidator.revalidate();
    } catch (err: unknown) {
      const description = err instanceof Error ? err.message : String(err);
      toast({ title: "Upload failed", description, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <EditorSection title={t("ui.dashboardCrm", "Dashboard CRM")}>
      <div className="space-y-6 max-w-lg">
        <div className="space-y-2">
          <Label>{t("ui.crmHeadshot", "CRM headshot")}</Label>
          <p className="text-xs text-muted-foreground">
            Square image recommended. Shown on the customer dashboard when this user is the assigned CRM.
          </p>
          <HeadshotPreview url={headshotUrl} name={user?.Key} />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onFileSelected}
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? t("ui.uploading", "Uploading…") : t("ui.uploadImage", "Upload image")}
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="calendly-link">{t("ui.calendlyLink", "Calendly link")}</Label>
          <Input
            id="calendly-link"
            type="url"
            placeholder="https://calendly.com/..."
            value={calendlyLink}
            onChange={(e) => setCalendlyLink(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Used for the Schedule a Meeting button on the customer dashboard when this user is the assigned CRM.
          </p>
          <Button type="button" disabled={savingCalendly} onClick={saveCalendlyLink}>
            {savingCalendly ? t("ui.saving", "Saving…") : t("ui.saveCalendlyLink", "Save Calendly link")}
          </Button>
        </div>
      </div>
    </EditorSection>
  );
}

function UserDashboardCrmFieldsExtension({ type, entity }: { type?: string; entity?: User }) {
  if (type !== "User" || !entity?.Id) {
    return null;
  }
  return <UserDashboardCrmFieldsInner userId={entity.Id} user={entity} />;
}

registerExtension("ApplyPortalTag", UserDashboardCrmFieldsExtension, "UserDashboardCrmFields");
